import { JournalAPI, UserReportAPI } from "generated-api"
import { checkIfSessionIsOrWillExpired } from "user/tools/common"
import {
  ICreateUserReportParams,
  IGetReportResult,
  IReportParameters,
  IReportsCampaignResult,
  IReportsCoordinateResult,
  IReportsCreativeResult,
  IReportsRepository,
  IReportsSSPOperatorResult,
  IReportsShapeResult,
  IUpdateUserReportParams,
  IUserReport
} from "./model"

export const reportsRPO: IReportsRepository = {
  async getResults(parameters: IReportParameters): Promise<IGetReportResult> {
    try {
      checkIfSessionIsOrWillExpired()
      const response = await JournalAPI.getApiJournal(
        undefined, // agencyId?: number, serverside is set by default with user's agency id
        undefined, // timezone
        parameters.filters.orderIds, // orderId?: Array<number>,
        parameters.filters.campaignIds, // campaignId?: Array<number>,
        parameters.filters.sspIds, // sspId?: Array<number>,
        parameters.filters.publisherIds, // publisherId?: Array<string>
        parameters.filters.screenIds, // screenId?: Array<string>,
        parameters.filters.countryIds, // country?: Array<string>,
        parameters.filters.stateIds, // state?: Array<string>,
        parameters.filters.cityIds, // city?: Array<string>,
        parameters.filters.postalCodes, // postalCode?: Array<string>,
        parameters.filters.venueTypeIds, // venueTypeId?: Array<number>,
        parameters.filters.mediaIds, // mediaId?: Array<number>,
        parameters.filters.mediaDimensions, // mediaDimension?: Array<string>,
        parameters.filters.mediaIsPortrait, // mediaIsPortrait?: boolean,
        parameters.filters.mediaIsVideo, // mediaIsVideo?: boolean,
        parameters.filters.weatherIds, // weatherId?: Array<number>,
        parameters.filters.startDate || undefined, // startDate?: string,
        parameters.filters.endDate || undefined, // endDate?: string,
        parameters.groupBys, // group?: Array
        parameters.sortBys // sort?: Array<'month'
      )
      let id = 0
      response.data = response.data.map(r => ({ ...r, id: id++ }))
      return response
    } catch (error) {
      console.error(error)
      return Promise.reject("Unexpected error while getting report results")
    }
  },
  async getCampaignsResults(campaignIds: number[]): Promise<IReportsCampaignResult[]> {
    const result = await this.getResults({
      filters: { campaignIds },
      groupBys: ["campaign_id"],
      sortBys: [],
      name: "",
      description: ""
    })
    return result.data as any
  },
  async getSSPOperatorResults(campaignId: number): Promise<IReportsSSPOperatorResult[]> {
    const result = await this.getResults({
      filters: { campaignIds: [campaignId] },
      groupBys: ["ssp_id", "publisher_id"],
      sortBys: [],
      name: "",
      description: ""
    })
    return result.data as any
  },
  async getCreativeResults(campaignId: number): Promise<IReportsCreativeResult[]> {
    const result = await this.getResults({
      filters: { campaignIds: [campaignId] },
      groupBys: ["media_id", "media_is_video", "media_dimension"],
      sortBys: [],
      name: "",
      description: ""
    })
    return result.data as any
  },
  async getShapeResults(campaignId: number): Promise<IReportsShapeResult[]> {
    const result = await this.getResults({
      filters: { campaignIds: [campaignId] },
      groupBys: ["city", "postal_code", "state", "country", "venue_type_id"],
      sortBys: [],
      name: "",
      description: ""
    })
    return result.data as any
  },
  async getCoordinateResults(campaignId: number): Promise<IReportsCoordinateResult[]> {
    const result = await this.getResults({
      filters: { campaignIds: [campaignId] },
      groupBys: ["campaign_coordinate_id"],
      sortBys: [],
      name: "",
      description: ""
    })
    return result.data as any
  },
  getMyUserReports(): Promise<IUserReport[]> {
    return new Promise<IUserReport[]>(async (resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        resolve(await UserReportAPI.getApiUserReportList())
      } catch (ex) {
        console.error("Unexpected error while getting my user reports", ex)
        reject("reports.get.my.user-report.error.unexpected")
      }
    })
  },
  createUserReport(params: ICreateUserReportParams): Promise<IUserReport> {
    return new Promise<IUserReport>(async (resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        resolve(await UserReportAPI.postApiUserReport(params))
      } catch (ex) {
        console.error("Unexpected error while creating new user report", ex)
        reject("reports.post.my.user-report.error.unexpected")
      }
    })
  },
  updateUserReport(params: IUpdateUserReportParams): Promise<IUserReport> {
    return new Promise<IUserReport>(async (resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        resolve(await UserReportAPI.putApiUserReport(params))
      } catch (ex) {
        console.error("Unexpected error while updating user report", ex)
        reject("reports.post.my.user-report.error.unexpected")
      }
    })
  },
  delUserReport(reportId: number): Promise<IUserReport> {
    return new Promise<IUserReport>(async (resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        resolve(await UserReportAPI.deleteApiUserReport(reportId))
      } catch (ex) {
        console.error("Unexpected error while deleting user report", ex)
        reject("reports.delete.my.user-report.error.unexpected")
      }
    })
  }
}
